import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Paperclip, Plus, X } from 'lucide-react'
import { MAIL_ATTACHMENT_EXTENSIONS, MAIL_EXPORT_LIMITS, MAX_MAIL_ATTACHMENT_BYTES } from '@symphenia/shared-types'

const ACCEPT = MAIL_ATTACHMENT_EXTENSIONS.map((e) => `.${e}`).join(',')
/** Über Microsoft Graph passen nur kleine Mails (AP-10 Nachtrag 1, 9.4). */
export const MICROSOFT_MAX_BYTES = 3 * 1024 * 1024

export function groesse(bytes: number): string {
  const format = (n: number) => new Intl.NumberFormat(navigator.language, { maximumFractionDigits: 1 }).format(n)
  return bytes >= 1024 * 1024 ? `${format(bytes / (1024 * 1024))} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

interface Props {
  dateien: File[]
  onChange: (dateien: File[]) => void
  /** Über Microsoft 365 warnt die Liste, sobald die Dateien die kleine Grenze sprengen. */
  ueberMicrosoft: boolean
}

/**
 * AP-10 Nachtrag 5, 3a — **eigene Dateien vom Rechner.** Export-Mail und
 * Belegversand (AP-32 Nachtrag 10) teilen dieselbe Auswahl. Hier wird nur
 * vorsortiert — Endung, Anzahl, Gesamtgröße; Endung **und** Inhalt prüft der Server.
 */
export function EigeneDateien({ dateien, onChange, ueberMicrosoft }: Props) {
  const { t } = useTranslation()
  const [hinweis, setHinweis] = useState<string | null>(null)
  const dateiwahl = useRef<HTMLInputElement>(null)
  const summe = dateien.reduce((s, d) => s + d.size, 0)

  const hinzufuegen = (liste: FileList | null) => {
    if (!liste?.length) return
    setHinweis(null)
    const neu = [...dateien]
    const abgelehnt: string[] = []
    for (const d of Array.from(liste)) {
      const endung = /\.([a-z0-9]+)$/i.exec(d.name)?.[1]?.toLowerCase() ?? ''
      if (!(MAIL_ATTACHMENT_EXTENSIONS as readonly string[]).includes(endung)) {
        abgelehnt.push(d.name)
        continue
      }
      if (neu.some((x) => x.name === d.name && x.size === d.size)) continue
      if (neu.length >= MAIL_EXPORT_LIMITS.files) {
        setHinweis(t('mailExport.tooManyFiles', { max: MAIL_EXPORT_LIMITS.files }))
        break
      }
      if (neu.reduce((s, x) => s + x.size, 0) + d.size > MAX_MAIL_ATTACHMENT_BYTES) {
        setHinweis(t('mailExport.filesTooLarge'))
        break
      }
      neu.push(d)
    }
    if (abgelehnt.length) setHinweis(t('mailExport.fileTypeNotAllowed', { names: abgelehnt.join(', ') }))
    onChange(neu)
  }

  return (
    <div className="space-y-1.5">
      {dateien.map((d, i) => (
        <div key={`${d.name}-${d.size}-${i}`} className="flex items-center gap-2 text-sm text-foreground">
          <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate" title={d.name}>
            {d.name}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{groesse(d.size)}</span>
          <button
            type="button"
            onClick={() => onChange(dateien.filter((_, j) => j !== i))}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive"
            aria-label={t('mailExport.removeFile', { name: d.name })}
            title={t('mailExport.removeFile', { name: d.name })}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <input
        ref={dateiwahl}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          hinzufuegen(e.target.files)
          e.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => dateiwahl.current?.click()}
          disabled={dateien.length >= MAIL_EXPORT_LIMITS.files}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('mailExport.addFile')}
        </button>
        <span className="text-xs text-muted-foreground">
          {dateien.length
            ? t('mailExport.filesSummary', { count: dateien.length, max: MAIL_EXPORT_LIMITS.files, size: groesse(summe) })
            : t('mailExport.filesHint')}
        </span>
      </div>
      {hinweis && <p className="text-xs text-destructive">{hinweis}</p>}
      {ueberMicrosoft && summe > MICROSOFT_MAX_BYTES && (
        <p className="rounded-md bg-amber-100 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">{t('mailExport.microsoftTooLarge')}</p>
      )}
    </div>
  )
}
